import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import { Archive as ArchiveIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { newImportService } from '../../services/newImportService';
import { formatDate } from '../../utils/formatters';

/**
 * Archive admin: list closed epics with date filter, select and move to ArchivedWorkItem collection.
 */
const ArchiveAdmin = () => {
  const [closedAfter, setClosedAfter] = useState('');
  const [closedBefore, setClosedBefore] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [epics, setEpics] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [archiving, setArchiving] = useState(false);
  const [sortBy, setSortBy] = useState('closedDate');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchClosedEpics = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const params = {};
      if (closedAfter) params.closedAfter = closedAfter;
      if (closedBefore) params.closedBefore = closedBefore;
      const res = await newImportService.getClosedEpics(params);
      if (res.success && Array.isArray(res.data)) {
        setEpics(res.data);
        setSelected(new Set());
      } else {
        setEpics([]);
        setError(res.message || 'Failed to load closed epics');
      }
    } catch (err) {
      setEpics([]);
      setError(err.response?.data?.message || err.message || 'Failed to load closed epics');
    } finally {
      setLoading(false);
    }
  }, [closedAfter, closedBefore]);

  useEffect(() => {
    fetchClosedEpics();
  }, []);

  const handleApplyFilter = () => {
    fetchClosedEpics();
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(new Set(epics.map(e => e.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = epics.length > 0 && selected.size === epics.length;
  const someSelected = selected.size > 0;

  const handleRequestSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const sortedEpics = useMemo(() => {
    const list = [...epics];
    list.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'closedDate') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (sortBy === 'id') {
        const aNum = parseInt(aVal, 10) || 0;
        const bNum = parseInt(bVal, 10) || 0;
        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
      }
      aVal = (aVal || '').toString().toLowerCase();
      bVal = (bVal || '').toString().toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [epics, sortBy, sortOrder]);

  const handleArchive = async () => {
    if (selected.size === 0) return;
    setArchiving(true);
    setError('');
    setSuccess('');
    try {
      const res = await newImportService.archiveEpics(Array.from(selected));
      if (res.success) {
        setSuccess(res.message || `Archived ${res.data?.archivedCount ?? selected.size} work item(s).`);
        setSelected(new Set());
        await fetchClosedEpics();
      } else {
        setError(res.message || 'Archive failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Archive failed');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Archive closed epics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        List epics in Closed state, optionally filter by closed date range. Select epics and click Archive to move them and all their descendants to the Archived work items collection.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Date filter (optional)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Closed after"
            type="date"
            value={closedAfter}
            onChange={(e) => setClosedAfter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Closed before"
            type="date"
            value={closedBefore}
            onChange={(e) => setClosedBefore(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 180 }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleApplyFilter}
            disabled={loading}
          >
            Apply filter
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="subtitle1">
            {epics.length} closed epic(s)
            {someSelected && ` · ${selected.size} selected`}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={archiving ? <CircularProgress size={20} color="inherit" /> : <ArchiveIcon />}
            onClick={handleArchive}
            disabled={archiving || !someSelected}
          >
            {archiving ? 'Archiving…' : 'Archive selected'}
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected && !allSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    disabled={epics.length === 0}
                    aria-label="Select all"
                  />
                </TableCell>
                <TableCell sortDirection={sortBy === 'id' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'id'}
                    direction={sortBy === 'id' ? sortOrder : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'title' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'title'}
                    direction={sortBy === 'title' ? sortOrder : 'asc'}
                    onClick={() => handleRequestSort('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'closedDate' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'closedDate'}
                    direction={sortBy === 'closedDate' ? sortOrder : 'asc'}
                    onClick={() => handleRequestSort('closedDate')}
                  >
                    Closed date
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'team' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortBy === 'team'}
                    direction={sortBy === 'team' ? sortOrder : 'asc'}
                    onClick={() => handleRequestSort('team')}
                  >
                    Team
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : epics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }} color="text.secondary">
                    No closed epics found. Adjust the date filter or run an import to see epics.
                  </TableCell>
                </TableRow>
              ) : (
                sortedEpics.map((epic) => (
                  <TableRow key={epic.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(epic.id)}
                        onChange={() => handleSelectOne(epic.id)}
                        aria-label={`Select epic ${epic.id}`}
                      />
                    </TableCell>
                    <TableCell>{epic.id}</TableCell>
                    <TableCell sx={{ maxWidth: 400 }} title={epic.title}>
                      <Typography variant="body2" noWrap>
                        {epic.title || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{epic.closedDate ? formatDate(epic.closedDate) : '—'}</TableCell>
                    <TableCell>{epic.team || '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ArchiveAdmin;
